/*
 * Sims page: simulation analytics dashboard.
 *
 * Headline KPIs (Total Sends, Failure Rate, Report Rate, 2nd-Stage
 * Rate, Repeat Clickers, Flagged Sims), clicks-by-date and reports
 * trends, click-by-sim treemap, time-of-click heatmap, device mix,
 * click-by-department bars, all-sims / users / send-history tables.
 * Scope filters narrow every aggregate; period filter swaps the window.
 *
 * Calculations:
 *   - clickRate = (clicked + clicked_2nd) / sends per scope/period.
 *   - reportRate = reported / sends.
 *   - secondStageRate = clicked_2nd / sends.
 *   - repeatClickers = count of users with a run of >=2 consecutive
 *     clicks across their tests ordered by dateSent (clicks in a row).
 *     Shared definition with riskScore.longestClickStreak.
 *   - sparklines pull the last 6 buckets from CLICKS_BY_MONTH[period].
 *   - scope filtering re-buckets the filtered SEND_LOG in-component via
 *     bucketByLabel(filteredLog, period); recomputation runs in useMemo
 *     keyed on (scope, period).
 *   - flagged-sims count drives the metric card and links into the
 *     Flagged Simulations page; computation is mirrored in
 *     FlaggedSimsPage to keep the count consistent.
 *
 * Postgres mapping:
 *   - canonical `simulations_sent` + `simulations` (id, name, theme,
 *     difficulty, secondStage, tags JSON, report_rate).
 *   - precomputed `sim_period_metrics` (org_id, period, scope_hash,
 *     kpis_json) refreshed nightly; client-side filtering only for
 *     interactive scope changes.
 *   - heatmap data: GROUP BY EXTRACT(HOUR FROM sent_at) DIV 4,
 *     EXTRACT(DOW FROM sent_at).
 *
 * New data points: sim.secondStage, sim.tags, sim.difficulty,
 * send.commsType, send.source (see data.js header).
 *
 * Integration hook: `useSimsAnalyticsQuery({ period, scope })` returning
 * { kpis, trends, heatmap, byDept, treemap, devices, table }.
 * Cache TTL 30 minutes; invalidate on period or scope change.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Button, Menu, MenuItem, LinearProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HeatmapCard from './summary/HeatmapCard';
import FunnelCard from './summary/FunnelCard';
import { computeFlaggedSims, buildFlagSimMap } from './FlaggedSimsPage';
import { FilterChip } from './MetaNav';
import { useSelector } from 'react-redux';
import { useGetFlaggedSimAcksQuery } from '../../../store/services/reportLayout.service';
import DataTable from './DataTable';
import { highClickUserColumns } from './HighRiskUsersPage';
import { sendLogColumns } from './SendHistoryPage';
import CountUp from 'react-countup';
import ReactApexChart from 'react-apexcharts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { SIMS, DEPTS, LOCS, CITIES, COUNTRIES, COMPANIES, STATES, GROUPS, USERS, PERIOD_DATA, BAR_DATA_BY_PERIOD, CLICKS_BY_MONTH, HIGH_RISK_USERS, SEND_LOG, CLICK_REPORT_TREND, DATA_EPOCH } from '../data';
import ActiveFilters from './ActiveFilters';
import SectionTitle from './SectionTitle';
import LureEffectivenessCard from './LureEffectivenessCard';
import TimeToClickCard from './TimeToClickCard';
import { tagsForSim, tagLabel } from '../tagAnalytics';
import { longestClickStreak } from '../riskScore';
import useDashboardData from '../hooks/useDashboardData';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Resolves the active [startMs, endMs] window for client-side chart filtering.
// A preset period is a window back from now; a custom range (falsy period +
// explicit ISO dates from the FilterBar pickers) uses those dates, so a custom
// range actually drives the charts instead of falling back to a fixed window.
function periodWindow(period, startDate, endDate) {
    const days = { Today: 1, '7d': 7, '30d': 30, '90d': 90, '6m': 182, '12m': 365 }[period];
    const now = Date.now();
    if (days != null) return [now - days * 86400000, now];
    const s = startDate ? Date.parse(startDate + 'T00:00:00Z') : (now - 90 * 86400000);
    const e = endDate ? Date.parse(endDate + 'T23:59:59Z') : now;
    return [s, e];
}

// Adaptive time-bucket granularity for the date charts: 30 days or less shows
// days, over 30 days but under ~6 months shows weeks, 6 months and up shows
// months - so the bar density scales with the selected window.
function bucketGranularity(winStart, winEnd) {
    const days = Math.round((winEnd - winStart) / 86400000);
    if (days <= 31) return 'day';
    if (days < 182) return 'week';
    return 'month';
}

function bucketKeyLabel(t, gran) {
    const d = new Date(t);
    if (gran === 'day') {
        return [d.toISOString().slice(0, 10), d.toLocaleString('en-GB', { day: 'numeric', month: 'short' })];
    }
    if (gran === 'week') {
        const monday = new Date(t - ((d.getUTCDay() + 6) % 7) * 86400000);
        return [monday.toISOString().slice(0, 10), `w/c ${monday.toLocaleString('en-GB', { day: 'numeric', month: 'short' })}`];
    }
    return [`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`, d.toLocaleString('en-GB', { month: 'short', year: '2-digit' })];
}

// Buckets the daily sim trend rollup ({ day, clicks, reports }) at the
// window-appropriate granularity, honouring the active period window. Mirrors
// the raw-log bucketing so the agg path and the scoped fallback look identical.
function bucketSimTrend(days, period, startDate, endDate) {
    if (!Array.isArray(days)) return null;
    const [winStart, winEnd] = periodWindow(period, startDate, endDate);
    const gran = bucketGranularity(winStart, winEnd);
    const counts = new Map();
    for (const r of days) {
        const t = Date.parse(r.day + 'T00:00:00Z');
        if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
        const [key, label] = bucketKeyLabel(t, gran);
        const cur = counts.get(key) || { key, label, n: 0, clicks: 0, reports: 0 };
        cur.n += (r.clicks || 0); cur.clicks += (r.clicks || 0); cur.reports += (r.reports || 0);
        counts.set(key, cur);
    }
    return [...counts.values()].sort((a, b) => a.key.localeCompare(b.key));
}

// Per-user 2nd-stage counts live on HIGH_RISK_USERS; lazy lookup so the
// High Click Users card can show them without recomputing from SEND_LOG,
// rebuilt after each data hydration.
let secondStageCache = null;
let secondStageEpoch = -1;
const SECOND_STAGE_BY_EMAIL = {
    get(email) {
        if (secondStageEpoch !== DATA_EPOCH) {
            secondStageCache = new Map(HIGH_RISK_USERS.map((u) => [u.email, u.secondStage]));
            secondStageEpoch = DATA_EPOCH;
        }
        return secondStageCache.get(email);
    },
};

const DEPT_OPTIONS = ['Department', 'Location', 'City', 'Country', 'Company', 'State', 'Group'];
const DEPT_DATA = {
    get Department() { return DEPTS; }, get Location() { return LOCS; }, get City() { return CITIES; },
    get Country() { return COUNTRIES; }, get Company() { return COMPANIES; }, get State() { return STATES; },
    get Group() { return GROUPS; },
};

// Exported for CoursesPage, which renders the same compact card tables.
export function TableHeader({ columns, template, actions }) {
    return (
        <Box sx={{
            display: 'grid', gridTemplateColumns: actions ? `92px ${template}` : template,
            px: 3, py: 1.5,
            bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
        }}>
            {actions && <Box aria-hidden="true" />}
            {columns.map((h) => (
                <Typography key={h} sx={{ fontSize: 12, fontWeight: 500, color: 'text.disabled' }}>
                    {h}
                </Typography>
            ))}
        </Box>
    );
}

// Left-aligned "View" affordance for drillable rows: replaces whole-row
// click so the click target is explicit, matching the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none', justifySelf: 'start',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

export function TableRow({ template, onClick, children }) {
    return (
        <Box
            sx={{
                display: 'grid', gridTemplateColumns: onClick ? `92px ${template}` : template,
                px: 3, py: 1.5,
                alignItems: 'center',
                borderBottom: '1px solid', borderColor: 'divider',
                transition: 'background 150ms',
                '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            {onClick && <RowViewButton onClick={onClick} />}
            {children}
        </Box>
    );
}

function DiffDots({ diff }) {
    return (
        <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: i <= diff ? (diff === 3 ? '#FF5252' : diff === 2 ? '#FFCA28' : '#66BB6A') : 'divider' }} />
            ))}
        </Box>
    );
}

function DotTrend({ data, color }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '3px', height: '100%', width: '100%' }}>
            {data.map((v, i) => {
                const pct = 15 + ((v - min) / range) * 85;
                const isLast = i === data.length - 1;
                return (
                    <Box key={i} sx={{ flex: 1, height: `${pct}%`, borderRadius: '2px', bgcolor: color, opacity: isLast ? (isDark ? 0.75 : 0.85) : (isDark ? 0.18 : 0.22) }} />
                );
            })}
        </Box>
    );
}

function MetricCard({ label, value, unit, sub, color, numColor, sparkData, actionLabel, onAction }) {
    return (
        <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.5 }}>
                            {label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: numColor || color || 'text.primary' }}>
                                <CountUp end={parseFloat(String(value).replace(/,/g, ''))} duration={1.4} decimals={String(value).includes('.') ? 1 : 0} separator="," />
                            </Typography>
                            {unit && <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{unit}</Typography>}
                        </Box>
                        {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.5 }}>{sub}</Typography>}
                    </Box>
                    {sparkData && (
                        <Box sx={{ flex: '0 0 36%', maxWidth: 100, alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
                            <DotTrend data={sparkData} color={color || '#6B7280'} />
                        </Box>
                    )}
                    {actionLabel && onAction && (
                        <Button
                            size="small" variant="outlined" color="secondary"
                            onClick={onAction}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                            sx={{
                                flexShrink: 0,
                                alignSelf: 'flex-end',
                                borderRadius: '6px', fontSize: 10, fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                px: 1.25, py: '3px',
                                borderColor: 'secondary.main', color: 'secondary.main',
                                '&:hover': { borderColor: 'secondary.main', backgroundColor: 'secondary.main', color: 'common.white' },
                                '& .MuiButton-endIcon': { transition: 'transform 200ms ease' },
                                '&:hover .MuiButton-endIcon': { transform: 'translateX(2px)' },
                            }}
                        >
                            {actionLabel}
                        </Button>
                    )}
                </Box>
            </MainCard>
        </motion.div>
    );
}

function ShimmerBar({ color, opacity = 1, pct, delay, isDark }) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        setValue(0);
        const t = setTimeout(() => setValue(pct), 150 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay]);

    return (
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height: 24,
                borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    background: color,
                    opacity,
                    transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: `${delay}s`,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)'}, transparent)`,
                        animation: value > 0 ? 'shine 2s ease-in-out' : 'none',
                        animationDelay: `${delay + 0.5}s`,
                        borderRadius: 1,
                    },
                },
                overflow: 'hidden',
                '@keyframes shine': {
                    '0%': { left: '-100%' },
                    '100%': { left: '100%' },
                },
            }}
        />
    );
}

// Maps the dropdown label in this widget to the field name on SEND_LOG events
// and the meta-list typeId used by MetaListPage.
const GROUP_FIELD = { Department: 'dept', Location: 'location', City: 'city', Country: 'country', Company: 'company', State: 'state', Group: 'adGroup' };
const GROUP_META_TYPE = { Department: 'department', Location: 'location', City: 'city', Country: 'country', Company: 'company', State: 'state', Group: 'adGroup' };

// Last 6 unique sims by send date. The hydrated SEND_LOG is sorted newest
// first, so the most recent sims sit at the head. Lazy + epoch-tracked so
// the selection follows each hydration.
let recentEventsCache = [];
let recentEventsEpoch = -1;
function recentSimEvents() {
    if (recentEventsEpoch !== DATA_EPOCH) {
        const seen = new Set();
        for (let i = 0; i < SEND_LOG.length && seen.size < 6; i++) {
            seen.add(SEND_LOG[i].simName);
        }
        recentEventsCache = SEND_LOG.filter((e) => seen.has(e.simName));
        recentEventsEpoch = DATA_EPOCH;
    }
    return recentEventsCache;
}

function ClickRateByDept({ theme, period, scope, drill, log, serverScope }) {
    const isDark = theme.palette.mode === 'dark';
    const [group, setGroup] = useState('Department');
    const [anchorEl, setAnchorEl] = useState(null);
    const dimType = GROUP_META_TYPE[group];
    const useAggScoped = !!serverScope;
    const useAggDims = (useAggScoped || !(scope && Object.keys(scope).length > 0)) && ['department', 'location', 'city', 'country', 'company', 'state'].includes(dimType);
    const dimsQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_dims', groupBy: dimType, scope: useAggScoped ? serverScope : undefined }, { skip: !useAggDims });
    const dimRows = useAggDims ? dimsQ.data?.simDims : null;

    const isScoped = scope && Object.keys(scope).length > 0 && !useAggScoped;

    const data = (() => {
        if (isScoped) {
            const scopeKey = scope.department ? 'dept' : scope.location ? 'location' : scope.country ? 'country' : null;
            const scopeVal = scopeKey ? scope[scopeKey] : null;
            const filtered = scopeVal
                ? USERS.filter((u) => u[scopeKey === 'dept' ? 'dept' : scopeKey] === scopeVal)
                : USERS;
            return [...filtered].sort((a, b) => b.risk - a.risk).slice(0, 5)
                .map((u) => ({ name: u.name, email: u.email, dept: u.dept, click: u.clicks, risk: u.risk }));
        }
        // Aggregate click rate per group from the active log slice when
        // provided (date + scope filters baked in), otherwise fall back
        // to RECENT_SIM_EVENTS which is the unfiltered most-recent slice.
        if (Array.isArray(dimRows) && dimRows.length) {
            return dimRows.map((r) => ({ name: r.dimValue, click: r.sent === 0 ? 0 : Math.round((r.clicks / r.sent) * 100), risk: 0 })).sort((a, b) => b.click - a.click);
        }
        const source = log && log.length > 0 ? log : recentSimEvents();
        const field = GROUP_FIELD[group] || 'dept';
        const buckets = {};
        for (const e of source) {
            const k = e[field];
            if (!k) continue;
            if (!buckets[k]) buckets[k] = { name: k, sent: 0, clicked: 0 };
            buckets[k].sent++;
            if (e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment') buckets[k].clicked++;
        }
        return Object.values(buckets)
            .map((b) => ({ name: b.name, click: b.sent === 0 ? 0 : Math.round((b.clicked / b.sent) * 100), risk: 0 }))
            .sort((a, b) => b.click - a.click);
    })();
    const TOP_N = 5;
    const displayed = data.slice(0, TOP_N);

    const sorted = [...data].sort((a, b) => b.click - a.click);
    const opacityFor = (name) => {
        const rank = sorted.findIndex((d) => d.name === name);
        return 1 - (rank / data.length) * 0.55;
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                            {isScoped ? 'Click Rate by User' : `Click Rate by ${group}`}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                            {isScoped ? 'Top 5 users by sim clicks' : 'Click rate across the last 6 sims sent'}
                        </Typography>
                    </Box>
                    {!isScoped && (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                sx={{
                                    borderRadius: '4px',
                                    borderColor: 'secondary.main',
                                    color: 'secondary.main',
                                    fontSize: 11,
                                    py: '3px',
                                    px: '10px',
                                    textTransform: 'none',
                                    backgroundColor: 'transparent',
                                    '&:hover': { backgroundColor: 'secondary.main', borderColor: 'secondary.main', color: 'common.white' },
                                }}
                            >
                                {group}
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                                PaperProps={{
                                    sx: { border: '1px solid', borderColor: 'secondary.main', borderRadius: '4px', boxShadow: 'none', mt: 0.5 },
                                }}
                            >
                                {DEPT_OPTIONS.map((o) => (
                                    <MenuItem
                                        key={o}
                                        onClick={() => { setGroup(o); setAnchorEl(null); }}
                                        sx={{
                                            fontSize: 11,
                                            borderRadius: '4px',
                                            mx: 0.5,
                                            color: o === group ? 'common.white' : 'secondary.main',
                                            backgroundColor: o === group ? 'secondary.main' : 'transparent',
                                            '&:hover': { backgroundColor: 'secondary.main', color: 'common.white' },
                                        }}
                                    >
                                        {o}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={group}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                {displayed.map((d, i) => (
                                    <Box
                                        key={d.email || d.name}
                                        onClick={() => isScoped && drill ? drill('user-detail', d.name, { type: 'user', value: d.email || d.name }) : undefined}
                                        sx={{ cursor: isScoped ? 'pointer' : 'default', '&:hover .seg-label': { color: isScoped ? 'primary.main' : 'text.primary' } }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography className="seg-label" sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', transition: 'color 150ms' }}>
                                                    {d.name}
                                                </Typography>
                                                {!isScoped && d.users != null && (
                                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                                        {d.users} users
                                                    </Typography>
                                                )}
                                                {isScoped && d.dept && (
                                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                                        {d.dept}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>
                                                {d.click}{isScoped ? '' : '%'}
                                            </Typography>
                                        </Box>
                                        <ShimmerBar key={`${period}-${d.name}`} color={theme.palette.primary.main} opacity={opacityFor(d.name)} pct={(d.click / (sorted[0]?.click || 1)) * 100} delay={i * 0.15} isDark={isDark} />
                                    </Box>
                                ))}
                            </Box>
                        </motion.div>
                    </AnimatePresence>
                </Box>
            </Box>
        </MainCard>
    );
}

function DevicePieCard({ theme, period, log, deviceAgg, startDate, endDate }) {
    const isDark = theme.palette.mode === 'dark';
    const secondary = theme.palette.secondary.main;
    const pd = PERIOD_DATA[period] || PERIOD_DATA['90d'];
    const devices = React.useMemo(() => {
        if (Array.isArray(deviceAgg) && deviceAgg.length) {
            const m = { Desktop: 0, Mobile: 0, Tablet: 0, Unknown: 0 };
            for (const d of deviceAgg) { if (m[d.device] != null) m[d.device] += d.clicks; else m.Unknown += d.clicks; }
            return ['Desktop', 'Mobile', 'Tablet', 'Unknown'].map((label) => ({ label, n: m[label] }));
        }
        // No event log = no device data; render an honest empty split rather
        // than an invented percentage mix.
        if (!log) return ['Desktop', 'Mobile', 'Tablet', 'Unknown'].map((label) => ({ label, n: 0 }));
        const [winStart, winEnd] = periodWindow(period, startDate, endDate);
        const counts = { Desktop: 0, Mobile: 0, Tablet: 0, Unknown: 0 };
        for (const e of log) {
            const t = Date.parse(e.dateSent + 'T00:00:00Z');
            if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
            if (!(e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment')) continue;
            const d = e.device || 'Unknown';
            if (counts[d] != null) counts[d] += 1; else counts.Unknown += 1;
        }
        return ['Desktop', 'Mobile', 'Tablet', 'Unknown'].map((label) => ({ label, n: counts[label] }));
    }, [log, period, pd, deviceAgg]);
    const total = devices.reduce((s, d) => s + d.n, 0);
    const [centre, setCentre] = useState({ value: total, label: 'Total clicks' });

    useEffect(() => { setCentre({ value: total, label: 'Total clicks' }); }, [total]);

    const colors = [
        secondary,
        secondary + 'CC',
        secondary + '88',
        secondary + '44',
    ];

    const options = {
        chart: {
            type: 'donut',
            background: 'transparent',
            animations: { enabled: true, speed: 700 },
            events: {
                dataPointMouseEnter: (_e, _ctx, config) => {
                    const d = devices[config.dataPointIndex];
                    if (d) setCentre({ value: d.n, label: d.label });
                },
                dataPointMouseLeave: () => setCentre({ value: total, label: 'Total clicks' }),
            },
        },
        colors,
        labels: devices.map((d) => d.label),
        plotOptions: {
            pie: {
                donut: {
                    size: '68%',
                    labels: { show: false },
                },
                expandOnClick: false,
            },
        },
        stroke: { width: 3, colors: [theme.palette.background.paper] },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false },
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                <Box sx={{ mb: 1.5, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        Clicks by Device
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Device breakdown across all simulations
                    </Typography>
                </Box>

                {/* Donut, centred, scaled up */}
                <Box
                    sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                    onMouseLeave={() => setCentre({ value: total, label: 'Total clicks' })}
                >
                    <ReactApexChart key={`${period}-${total}`} options={options} series={devices.map((d) => d.n)} type="donut" height={220} width={220} />
                    <Box sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -56%)',
                        textAlign: 'center', pointerEvents: 'none', width: 80,
                    }}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={centre.value}
                                initial={{ opacity: 0, y: 3 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -3 }}
                                transition={{ duration: 0.15 }}
                            >
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
                                    {centre.value}
                                </Typography>
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', mt: '2px', lineHeight: 1.2 }}>
                                    {centre.label}
                                </Typography>
                            </motion.div>
                        </AnimatePresence>
                    </Box>
                </Box>

                {/* Stats row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', flexShrink: 0, pt: 1 }}>
                    {devices.map((d, i) => {
                        const pct = Math.round((d.n / total) * 100);
                        return (
                            <Box key={d.label} sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: colors[i], flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>{d.label}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', color: 'text.primary', lineHeight: 1 }}>
                                    {pct}<Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>%</Box>
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: '2px' }}>{d.n} clicks</Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </MainCard>
    );
}

function ClickRateChart({ theme, log, simsAgg }) {
    const primary = theme.palette.primary.main;
    // Top 5 sims by click rate. Each segment's flex weight is the click %
    // so width is directly proportional to click rate. When a scoped log
    // is supplied, ranking is recomputed against only those events so
    // date + scope filters narrow the chart.
    const TOP_N = 5;
    const topSims = React.useMemo(() => {
        if (Array.isArray(simsAgg) && simsAgg.length) {
            const rows = simsAgg.filter((r) => r.sent >= 3).map((r) => ({ name: r.simName, click: Math.round((r.clicks / r.sent) * 100), lv: SIMS.find((x) => x.name === r.simName)?.lv || 'med' })).sort((a, b) => b.click - a.click).slice(0, TOP_N);
            if (rows.length) return rows;
        }
        if (!log) return [...SIMS].sort((a, b) => parseInt(b.click) - parseInt(a.click)).slice(0, TOP_N);
        const counts = new Map();
        for (const e of log) {
            const c = counts.get(e.simName) || { sent: 0, clicked: 0 };
            c.sent += 1;
            if (e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment') c.clicked += 1;
            counts.set(e.simName, c);
        }
        const rows = [...counts.entries()]
            .filter(([, c]) => c.sent >= 3)
            .map(([name, c]) => ({ name, click: Math.round((c.clicked / c.sent) * 100), lv: SIMS.find((s) => s.name === name)?.lv || 'med' }))
            .sort((a, b) => b.click - a.click)
            .slice(0, TOP_N);
        return rows.length > 0 ? rows : [...SIMS].sort((a, b) => parseInt(b.click) - parseInt(a.click)).slice(0, TOP_N);
    }, [log, simsAgg]);
    const values = topSims.map((s) => parseInt(s.click));
    const max = Math.max(...values);
    const min = Math.min(...values);
    const colors = values.map((v) => {
        const t = (v - min) / (max - min || 1);
        const opacity = 0.22 + t * 0.78;
        return primary + Math.round(opacity * 255).toString(16).padStart(2, '0');
    });

    const total = values.reduce((a, b) => a + b, 0);

    // Approximate column pixel width from its flex weight. Drives whether the
    // sim name fits below the percentage. We assume a typical ~480px chart
    // width, being conservative here means we'd rather hide the name than
    // show one that truncates. Two lines available: a sim name fits if any of
    // its space-delimited tokens (or the whole label) fits in the column;
    // otherwise the line-clamp with ellipsis catches overflow.
    const APPROX_CHART_PX = 480;
    const CHAR_PX = 5.6;       // ~width of a font-size 10 / weight 500 char
    const NAME_PADDING_PX = 16; // breathing room either side of the label
    const MAX_NAME_LINES = 2;
    const longestTokenPx = (name) =>
        Math.max(...name.split(/\s+/).map((tok) => tok.length)) * CHAR_PX;

    return (
        <Box sx={{ display: 'flex', gap: '4px', height: '100%', width: '100%' }}>
            {topSims.map((s, i) => {
                const proportion = parseInt(s.click) / total;
                const showLabel = proportion >= 0.08;
                const colWidthPx = proportion * APPROX_CHART_PX;
                // Show the name as long as the longest single word can fit
                // on one line, words shorter than that can wrap, longer ones
                // would force a mid-word break and look broken.
                const tokenFitPx = longestTokenPx(s.name);
                const showName = showLabel && tokenFitPx + NAME_PADDING_PX < colWidthPx;
                return (
                    <Tooltip
                        key={s.name}
                        title={
                            <Box>
                                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{s.name}</Typography>
                                <Typography sx={{ fontSize: 11 }}>Click Rate: {parseInt(s.click)}%</Typography>
                                <Typography sx={{ fontSize: 11 }}>Risk: {{ high: 'High', med: 'Medium', low: 'Low' }[s.lv] || 'Medium'}</Typography>
                            </Box>
                        }
                        arrow
                        placement="top"
                    >
                        <Box
                            sx={{
                                flex: parseInt(s.click),
                                borderRadius: '6px',
                                bgcolor: colors[i],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                cursor: 'default',
                                transition: 'filter 150ms',
                                '&:hover': { filter: 'brightness(1.12)' },
                            }}
                        >
                            {showLabel && (
                                <Box sx={{ textAlign: 'center', px: 0.5, minWidth: 0 }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                                        <Box component="span" sx={{ fontSize: 15, fontWeight: 800, color: theme.palette.primary.contrastText }}>
                                            {parseInt(s.click)}
                                        </Box>
                                        <Box component="span" sx={{ fontSize: 10, fontWeight: 500, color: theme.palette.primary.contrastText, opacity: 0.55, ml: '1px' }}>
                                            %
                                        </Box>
                                    </Box>
                                    {showName && (
                                        <Box sx={{
                                            fontSize: 10,
                                            fontWeight: 500,
                                            color: theme.palette.primary.contrastText,
                                            opacity: 0.7,
                                            mt: '2px',
                                            lineHeight: 1.2,
                                            display: '-webkit-box',
                                            WebkitLineClamp: MAX_NAME_LINES,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}>
                                            {s.name}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

const STATUS_META = {
    clicked_2nd: { label: '2nd Stage', bg: 'rgba(255,82,82,.1)',   text: '#E53935', border: 'rgba(255,82,82,.25)' },
    clicked:     { label: 'Clicked',   bg: 'rgba(255,202,40,.12)', text: '#C68A00', border: 'rgba(255,202,40,.3)' },
    no_click:    { label: 'No click',  bg: 'rgba(0,0,0,0.04)',     text: '#6B7280', border: 'rgba(0,0,0,.1)' },
    reported:    { label: 'Reported',  bg: 'rgba(102,187,106,.1)',   text: '#388E3C', border: 'rgba(102,187,106,.25)' },
};

function ClicksByMonthChart({ period, theme, log, trendsAgg, startDate, endDate }) {
    // Live bucketing when a scoped log slice is provided so date + scope
    // filters narrow the bars; falls back to the precomputed series for
    // the unfiltered org-wide view.
    const data = useMemo(() => {
        if (Array.isArray(trendsAgg)) { const b = bucketSimTrend(trendsAgg, period, startDate, endDate); if (b && b.length) return b; }
        if (!log) return CLICKS_BY_MONTH[period] || CLICKS_BY_MONTH['90d'];
        const [winStart, winEnd] = periodWindow(period, startDate, endDate);
        const gran = bucketGranularity(winStart, winEnd);
        const counts = new Map();
        for (const e of log) {
            const t = Date.parse(e.dateSent + 'T00:00:00Z');
            if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
            const [key, label] = bucketKeyLabel(t, gran);
            const isClick = e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment';
            const isReport = e.status === 'reported';
            const cur = counts.get(key) || { key, label, n: 0, reports: 0 };
            if (isClick) cur.n += 1;
            if (isReport) cur.reports += 1;
            counts.set(key, cur);
        }
        return [...counts.values()].sort((a, b) => a.key.localeCompare(b.key));
    }, [log, period, trendsAgg, startDate, endDate]);
    const isDark = theme.palette.mode === 'dark';
    const color = theme.palette.secondary.main;
    const reportsColor = '#66BB6A';
    const tickColor = isDark ? '#5A6170' : '#A0A8B8';
    const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
    // Dual-bar option toggles a second series (reports) alongside clicks.
    // Defaults to dual; per-period preference persists in localStorage, so the
    // single view only sticks when the user has explicitly chosen it.
    const [dual, setDual] = useState(() => localStorage.getItem('rbx_clicksbydate_dual') !== '0');
    // When live-bucketed, `reports` already lives on each datum; a datum
    // without it shows 0 reported - never an invented series.
    const dualData = useMemo(() => data.map((d) => ({
        ...d,
        reports: d.reports != null ? d.reports : 0,
    })), [data]);

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', minHeight: 320, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        Clicks by Date
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        {dual ? 'Clicks vs reports per period' : 'Email link clicks across the period'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {dual && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px' }} />
                                Clicks
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: reportsColor, borderRadius: '2px' }} />
                                Reports
                            </Box>
                        </Box>
                    )}
                    <Box
                        component="button"
                        onClick={() => setDual((d) => { const n = !d; localStorage.setItem('rbx_clicksbydate_dual', n ? '1' : '0'); return n; })}
                        sx={{
                            border: '1px solid', borderColor: dual ? 'secondary.main' : 'divider',
                            bgcolor: dual ? 'secondary.main' : 'transparent',
                            color: dual ? 'common.white' : 'text.secondary',
                            fontSize: 10, fontWeight: 600,
                            px: 1, py: '3px',
                            borderRadius: '4px', cursor: 'pointer',
                            fontFamily: 'inherit',
                            transition: 'all 150ms',
                        }}
                    >{dual ? 'Single' : 'Dual'}</Box>
                </Box>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, pt: 1, pb: 2, px: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dual ? dualData : data} barCategoryGap="12%" margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="4 4" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: tickColor, fontFamily: 'Inter' }} tickMargin={8} />
                        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: tickColor, fontFamily: 'Inter' }} />
                        <RechartsTooltip
                            cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                            contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`, borderRadius: 6, fontSize: 11, fontFamily: 'Inter', color: theme.palette.text.primary }}
                            itemStyle={{ color: theme.palette.text.primary }}
                            labelStyle={{ color: theme.palette.text.secondary, fontWeight: 600 }}
                        />
                        <Bar dataKey="n" name="Clicks" fill={color} radius={[4, 4, 0, 0]} maxBarSize={dual ? 32 : 64} isAnimationActive animationDuration={600} animationEasing="ease-out" />
                        {dual && <Bar dataKey="reports" name="Reports" fill={reportsColor} radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive animationDuration={600} animationEasing="ease-out" />}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </MainCard>
    );
}

function HighRiskUsersTable({ drill }) {
    const TOP_N = 6;
    const preview = HIGH_RISK_USERS.slice(0, TOP_N);
    const TH = ({ children }) => (
        <Box component="th" sx={{ fontSize: 12, fontWeight: 500, color: 'text.disabled', px: 2, py: 1.5, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
            {children}
        </Box>
    );
    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>High Click Users</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Top {TOP_N} of {HIGH_RISK_USERS.length}. Click a row to view detail.</Typography>
                </Box>
                <Button
                    size="small" variant="outlined"
                    onClick={() => drill('high-risk-users', 'High Click Users')}
                    sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                >
                    View all ({HIGH_RISK_USERS.length})
                </Button>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr"><Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', width: '1px' }} /><TH>Name</TH><TH>Sent</TH><TH>Clicked</TH><TH>Second Stage Clicked</TH></Box>
                        </Box>
                        <Box component="tbody">
                            {preview.map((u) => (
                                <Box component="tr" key={u.email}
                                    sx={{ borderBottom: '1px solid', borderColor: 'divider', transition: 'background 150ms', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}>
                                    <Box component="td" sx={{ px: 2, py: 1.5, width: '1px', whiteSpace: 'nowrap' }}>
                                        <RowViewButton onClick={() => drill('user-detail', `${u.firstName} ${u.lastName}`, { type: 'user', value: u.email })} />
                                    </Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>{u.firstName} {u.lastName}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{u.sent}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 700, color: u.clicked > 2 ? '#FF5252' : '#FFCA28' }}>{u.clicked}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 700, color: u.secondStage > 0 ? '#FF5252' : 'text.secondary' }}>{u.secondStage}</Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
}

function SendHistoryCard({ drill, logs, total }) {
    const allLogs = logs || SEND_LOG;
    // SEND_LOG is sorted newest-first, so the head is the last 10 events.
    const preview = allLogs.slice(0, 10);

    return (
        <DataTable
            title="Send History"
            subtitle="Last 10 delivery events"
            columns={sendLogColumns()}
            rows={preview}
            rowKey={(e) => `${e.email}-${e.simName}-${e.sentAt}`}
            onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
            initialSort={{ by: 'sent', dir: 'desc' }}
            storageKey="rbx-sims-send-history-card-cols"
            pagination={false}
            emptyMessage="No delivery events in scope"
            headerActions={
                <Button size="small" variant="outlined" onClick={() => drill('send-history', 'Simulation Send History', null)}
                    sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}>
                    View all ({(total != null ? total : allLogs.length).toLocaleString()})
                </Button>
            }
        />
    );
}



function ReportingTrendCard({ period, theme, log, trendsAgg, startDate, endDate }) {
    const data = useMemo(() => {
        if (Array.isArray(trendsAgg)) { const b = bucketSimTrend(trendsAgg, period, startDate, endDate); if (b && b.length) return b; }
        if (!log) return CLICK_REPORT_TREND[period] || CLICK_REPORT_TREND['90d'];
        const [winStart, winEnd] = periodWindow(period, startDate, endDate);
        const gran = bucketGranularity(winStart, winEnd);
        const counts = new Map();
        for (const e of log) {
            const t = Date.parse(e.dateSent + 'T00:00:00Z');
            if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
            const [key, label] = bucketKeyLabel(t, gran);
            const cur = counts.get(key) || { key, label, clicks: 0, reports: 0 };
            if (e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment') cur.clicks += 1;
            if (e.status === 'reported') cur.reports += 1;
            counts.set(key, cur);
        }
        return [...counts.values()].sort((a, b) => a.key.localeCompare(b.key));
    }, [log, period, trendsAgg, startDate, endDate]);
    const isDark = theme.palette.mode === 'dark';
    const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
    const labelColor = theme.palette.text.primary;

    // ApexCharts at height="100%" measures its parent once on mount. Inside a
    // grid item that stretches to match the sibling table, the card's final
    // height isn't settled until after the fadeUp entrance animation, so the
    // chart otherwise locks to minHeight and leaves the card half-empty. A
    // resize on the next frame forces it to re-measure the settled height.
    useEffect(() => {
        const id = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        return () => cancelAnimationFrame(id);
    }, [period]);

    const options = {
        chart: { type: 'line', background: 'transparent', toolbar: { show: false }, animations: { enabled: false } },
        colors: ['#FF5252', '#66BB6A'],
        stroke: { curve: 'smooth', width: [2.5, 2.5] },
        fill: {
            type: ['gradient', 'gradient'],
            gradient: {
                type: 'vertical',
                colorStops: [
                    [
                        { offset: 0,   color: '#FF6B7A', opacity: 0.22 },
                        { offset: 50,  color: '#FF5252', opacity: 0.08 },
                        { offset: 100, color: '#FF5252', opacity: 0 },
                    ],
                    [
                        { offset: 0,   color: '#4ADE80', opacity: 0.18 },
                        { offset: 50,  color: '#66BB6A', opacity: 0.07 },
                        { offset: 100, color: '#66BB6A', opacity: 0 },
                    ],
                ],
            },
        },
        markers: {
            size: [0, 0],
            hover: { size: 5 },
            colors: ['#FF5252', '#66BB6A'],
            strokeColors: theme.palette.background.paper,
            strokeWidth: 2,
            fillOpacity: 1,
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: data.map((d) => d.label),
            labels: { style: { fontSize: '10px', fontFamily: 'Inter', colors: labelColor }, rotate: 0, hideOverlappingLabels: true },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { offsetX: -14, style: { fontSize: '10px', fontFamily: 'Inter', colors: labelColor } } },
        grid: { borderColor: gridColor, xaxis: { lines: { show: false } }, padding: { left: 4, right: 4, bottom: 0 } },
        legend: { show: false },
        tooltip: { theme: isDark ? 'dark' : 'light', style: { fontSize: '11px', fontFamily: 'Inter' } },
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', minHeight: 260, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 3, pb: 1.25, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        Reporting Trend
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Click count vs report count over the period</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {[{ color: '#FF5252', label: 'Clicks' }, { color: '#66BB6A', label: 'Reports' }].map(({ color, label }) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                            <Box sx={{ width: 20, height: 2, bgcolor: color, borderRadius: '1px', flexShrink: 0 }} />
                            {label}
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box sx={{ flex: 1, minHeight: 180, px: 3, pb: 3 }}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                    <Box sx={{ position: 'absolute', inset: 0 }}>
                        <ReactApexChart
                            key={period}
                            options={{ ...options, chart: { ...options.chart, height: '100%' } }}
                            series={[
                                { name: 'Clicks',  data: data.map((d) => d.clicks),  type: 'area' },
                                { name: 'Reports', data: data.map((d) => d.reports), type: 'area' },
                            ]}
                            type="line"
                            height="100%"
                            width="100%"
                        />
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
}


// Compact autocomplete that adds a scope filter (sim or user) to the current
// page via drill(), so it lands as a removable ActiveFilters chip and stacks
// with drilled-in scopes. Values already in scope are hidden from the options.
// Exported for CoursesPage, which renders the same pickers for course + user.

export default function SimsPage({ go, drill, openDrawer, period = '90d', startDate, endDate, scope, removeScope, search = '', vis: visProp, orderedGroup: orderedGroupProp, dataReady = true }) {
    const vis = visProp || (() => true);
    const orderedGroup = orderedGroupProp || ((group) => {
        // Fallback used when the page is rendered standalone (e.g. tests).
        // Keys match the section titles in widgetRegistry.js so a single
        // rename there flows through here without breaking the page.
        const defaults = {
            'Performance':        ['metric-sends', 'metric-click-rate', 'metric-second-stage', 'metric-repeat-clickers', 'metric-report-rate', 'metric-flagged'],
            'Activity & breakdowns': ['clicks-by-month', 'device-breakdown', 'click-by-dept', 'click-by-sim', 'heatmap-card', 'sim-engagement'],
            'Click and report': ['report-trend', 'high-risk'],
            'Detail tables':      ['all-sims', 'users-table', 'send-history'],
        };
        return (defaults[group] || []).map((id) => ({ id }));
    });
    const theme = useTheme();
    const pd = PERIOD_DATA[period] || PERIOD_DATA['90d'];

    // Derive scoped data sets used across multiple widgets. Filters AND
    // across categories and OR within a category, e.g. (dept = Finance OR
    // Marketing) AND (country = US). Search is applied last and matches any
    // visible field.
    const s = scope || {};
    const q = search.trim().toLowerCase();
    const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };
    const matchesUserScope = (rec, vs) => {
        if (!vs) return true;
        const arr = Array.isArray(vs) ? vs : [vs];
        const full = `${rec.firstName ?? ''} ${rec.lastName ?? ''}`.trim();
        return arr.some((v) => rec.email === v || full === v || rec.name === v);
    };
    const matchesSearch = (hay) => !q || hay.toLowerCase().includes(q);
    const tagScope = s.tag ? (Array.isArray(s.tag) ? s.tag : [s.tag]) : null;
    const scopedLogs = SEND_LOG.filter((e) => {
        if (!matchesUserScope(e, s.user))                                     return false;
        if (!isIn(e.simName,  s.sim))                                         return false;
        if (!isIn(e.dept,     s.department))                                  return false;
        if (!isIn(e.location, s.location))                                    return false;
        if (!isIn(e.country,  s.country))                                     return false;
        if (!isIn(e.city,    s.city))       return false;
        if (!isIn(e.company, s.company))    return false;
        if (!isIn(e.state,   s.state))      return false;
        if (!isIn(e.adGroups, s.adGroup))    return false;
        if (tagScope) {
            const simTags = tagsForSim(e.simName);
            if (!tagScope.some((t) => simTags.includes(t))) return false;
        }
        if (!matchesSearch(`${e.firstName} ${e.lastName} ${e.email} ${e.simName} ${e.dept} ${e.location} ${e.city} ${e.country} ${e.state} ${e.company}`)) return false;
        return true;
    });
    const scopedSimNames  = new Set(scopedLogs.map((e) => e.simName));
    const scopedSims      = SIMS.filter((sim) => Object.keys(s).length === 0 || scopedSimNames.has(sim.name));
    const scopedHighRisk = USERS.filter((u) => {
        if (!matchesUserScope(u, s.user))                                     return false;
        if (!isIn(u.dept,     s.department))                                  return false;
        if (!isIn(u.location, s.location))                                    return false;
        if (!isIn(u.country,  s.country))                                     return false;
        if (!isIn(u.city,    s.city))       return false;
        if (!isIn(u.company, s.company))    return false;
        if (!isIn(u.state,   s.state))      return false;
        if (!isIn(u.adGroups, s.adGroup))    return false;
        if (!matchesSearch(`${u.name} ${u.email} ${u.dept} ${u.location}`))    return false;
        return true;
    });

    let scopedUsersTable = HIGH_RISK_USERS.filter((u) => {
        if (!matchesUserScope(u, s.user))    return false;
        if (!isIn(u.dept,     s.department)) return false;
        if (!isIn(u.location, s.location))   return false;
        if (!isIn(u.country,  s.country))    return false;
        if (!isIn(u.city,     s.city))      return false;
        if (!isIn(u.company,  s.company))   return false;
        if (!isIn(u.state,    s.state))     return false;
        if (!isIn(u.adGroups,  s.adGroup))   return false;
        return true;
    });

    // Stage depth = share of recipients that reached the credential
    // entry stage. 2nd-stage events / sent. Backs the "Stage depth"
    // factor on the Home risk score.
    const secondStageRate = (() => {
        const sent = scopedLogs.length;
        if (sent === 0) return '0.0';
        const second = scopedLogs.filter((e) => e.status === 'clicked_2nd').length;
        return ((second / sent) * 100).toFixed(1);
    })();

    // Repeat clickers = users whose clicks land back-to-back. We group each
    // user's tests, order them by send date and look for a run of two or
    // more consecutive clicks: an isolated click surrounded by passes is not
    // a repeat offence. Same "clicks in a row" definition as the repeat
    // offender score factor in riskScore.js (longestClickStreak).
    const repeatClickerCount = (() => {
        const byEmail = {};
        for (const e of scopedLogs) {
            if (!e.dateSent) continue;
            (byEmail[e.email] || (byEmail[e.email] = [])).push(e);
        }
        let count = 0;
        for (const sims of Object.values(byEmail)) {
            if (longestClickStreak(sims) >= 2) count += 1;
        }
        return count;
    })();

    // Drill helper for factor-bound KPIs: pushes a factor-detail page
    // with the factor id as context so the destination renders the deep
    // dive for THAT pillar.
    const exploreFactor = (factorId, factorLabel) => drill && drill('factor-detail', factorLabel, { type: 'factor', value: factorId });

    // Aggregate-first KPIs: when no scope filter is active, headline numbers come
    // from the precomputed sim rollups (period-windowed, uncapped) via the BFF;
    // any active scope falls back to the client-side scopedLogs computation below.
    const simScopeActive = (() => { const sc = scope || {}; return Object.keys(sc).some((k) => { const v = sc[k]; return Array.isArray(v) ? v.length > 0 : !!v; }); })();
    // User-dimension scope is pushed to the server (uncapped); tag/search stay client-side (raw, capped).
    const normScope = (v) => (v == null ? undefined : Array.isArray(v) ? (v.length ? v : undefined) : [v]);
    const serverScope = { department: normScope(s.department), location: normScope(s.location), city: normScope(s.city), country: normScope(s.country), state: normScope(s.state), company: normScope(s.company), adGroup: normScope(s.adGroup), user: normScope(s.user) };
    const hasServerScope = Object.values(serverScope).some(Boolean);
    // sim scope has no server-side counterpart (DashboardRequest.Scope carries
    // user dimensions only), so it forces the client-side computation paths
    // like tag/search do - never sent to the server where it would silently
    // not filter.
    const clientOnlyScope = !!(tagScope || q || normScope(s.sim));
    const simsAggQ = useDashboardData({ metric: 'simulations', aggregation: 'sims', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const simRows = (!clientOnlyScope && Array.isArray(simsAggQ.data?.sims)) ? simsAggQ.data.sims : null;
    const riskAggQ = useDashboardData({ metric: 'simulations', aggregation: 'risk', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const simAgg = simRows ? (() => {
        const sent = simRows.reduce((a, r) => a + (r.sent || 0), 0);
        const clicks = simRows.reduce((a, r) => a + (r.clicks || 0), 0);
        const reports = simRows.reduce((a, r) => a + (r.reports || 0), 0);
        const landings = simRows.reduce((a, r) => a + (r.landings || 0), 0);
        const pct = (n) => (sent ? ((n / sent) * 100).toFixed(1) : '0.0');
        const flagged = simRows.filter((r) => r.sent >= 10 && r.clicks / r.sent >= 0.3).length;
        return { sent, clicks, reports, landings, clickRate: pct(clicks), reportRate: pct(reports), secondStage: pct(landings), flagged };
    })() : null;
    const repeatAgg = (!clientOnlyScope && riskAggQ.data?.risk?.repeatClickers != null) ? riskAggQ.data.risk.repeatClickers : null;
    // Lure effectiveness: roll the per-sim agg rows up to tag level (a sim can
    // carry several tags) so the leaderboard matches clickRateByTag's shape.
    const lureAggQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_lure', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const lureAggRows = (!clientOnlyScope && Array.isArray(lureAggQ.data?.simLure)) ? lureAggQ.data.simLure.map((r) => ({ tag: r.tag, label: r.label, sends: r.sent, clicks: r.clicks, rate: r.sent ? (r.clicks / r.sent) * 100 : 0, reportRate: r.sent ? (r.reports / r.sent) * 100 : 0 })) : null;
    const lureTagRows = (!clientOnlyScope && Array.isArray(simRows)) ? (() => {
        const m = {};
        for (const r of simRows) {
            for (const t of (tagsForSim(r.simName) || [])) {
                const e = m[t] || (m[t] = { tag: t, label: tagLabel(t), sends: 0, clicks: 0, reports: 0 });
                e.sends += (r.sent || 0); e.clicks += (r.clicks || 0); e.reports += (r.reports || 0);
            }
        }
        return Object.values(m).map((e) => ({ ...e, rate: e.sends ? (e.clicks / e.sends) * 100 : 0, reportRate: e.sends ? (e.reports / e.sends) * 100 : 0 }));
    })() : null;
    const simTrendsQ = useDashboardData({ metric: 'simulations', aggregation: 'trends', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const simTrendAgg = (!clientOnlyScope && Array.isArray(simTrendsQ.data?.trends)) ? simTrendsQ.data.trends : null;
    const simHeatQ = useDashboardData({ metric: 'simulations', aggregation: 'heatmap', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const simHeatAgg = (!clientOnlyScope && Array.isArray(simHeatQ.data?.heatmap)) ? simHeatQ.data.heatmap : null;
    const simDeviceQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_device', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const simDeviceAgg = (!clientOnlyScope && Array.isArray(simDeviceQ.data?.simDevices)) ? simDeviceQ.data.simDevices : null;
    const simTtcQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_ttc', scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const simTtcAgg = (!clientOnlyScope && Array.isArray(simTtcQ.data?.simTtc)) ? simTtcQ.data.simTtc : null;
    const riskUsersQ = useDashboardData({ metric: 'simulations', aggregation: 'users', scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    if (hasServerScope && !clientOnlyScope && Array.isArray(riskUsersQ.data?.riskUsers)) scopedUsersTable = riskUsersQ.data.riskUsers;

    // No server-side flagged field exists (sim.flag is always false), so derive
    // the count with the same CRITERIA the Flagged Sims drill uses — keeps this
    // KPI in sync with the drill instead of reading an empty server field.
    // Acknowledged sims come from the tenancy-wide ack store the drill writes
    // to; sharing the cache entry means the figure updates the moment a flag
    // is acknowledged or restored.
    const rbAckTenancyId = Number(useSelector((st) => st.tenancy.tenancyId));
    const flagAcksQ = useGetFlaggedSimAcksQuery(rbAckTenancyId, { skip: !Number.isFinite(rbAckTenancyId) || rbAckTenancyId <= 0 });
    // Same query args as the drill page, so RTK serves both from one cache
    // entry and the KPI counts exactly what the drill lists.
    const flagSimsQ = useDashboardData({ metric: 'simulations', aggregation: 'sims' });
    const flaggedCount = useMemo(() => {
        const acks = flagAcksQ.data?.acks || {};
        return computeFlaggedSims(buildFlagSimMap(flagSimsQ.data?.sims)).filter((f) => !acks[f.sim.name]).length;
    }, [flagAcksQ.data, flagSimsQ.data]);

    const metrics = [
        { id: 'metric-sends',         label: 'Total Sends',    value: simAgg ? simAgg.sent.toLocaleString() : pd.funnelSent.toLocaleString(), unit: '',  sub: simAgg ? `${simAgg.clicks.toLocaleString()} of ${simAgg.sent.toLocaleString()} clicked` : pd.simSub,          color: 'secondary.main', numColor: theme.palette.mode === 'dark' ? '#ffffff' : '#111111',
          actionLabel: 'More', onAction: () => drill && drill('send-history', 'Simulation Send History') },
        { id: 'metric-click-rate',    label: 'Failure Rate',   value: simAgg ? simAgg.clickRate : pd.simClickRate,                unit: '%', sub: 'Across all sims',  color: '#FF5252',
          actionLabel: 'More', onAction: () => exploreFactor('fail-rate', 'Failure rate') },
        { id: 'metric-report-rate',   label: 'Report Rate',    value: simAgg ? simAgg.reportRate : pd.reportRate,                  unit: '%', sub: 'Of total sends',   color: '#66BB6A',
          actionLabel: 'More', onAction: () => drill && drill('send-history', 'Reported sends', { type: 'status', value: 'reported' }) },
        { id: 'metric-second-stage',  label: '2nd Stage Rate', value: simAgg ? simAgg.secondStage : secondStageRate,                unit: '%', sub: 'Credentials entered or download link clicked', color: '#FF5252',
          actionLabel: 'More', onAction: () => exploreFactor('stage-depth', 'Stage depth') },
        { id: 'metric-repeat-clickers', label: 'Repeat Clickers', value: repeatAgg != null ? repeatAgg.toLocaleString() : repeatClickerCount.toLocaleString(), unit: '', sub: 'Clicked 2+ tests in a row', color: '#FFCA28',
          actionLabel: 'More', onAction: () => exploreFactor('repeat', 'Repeat offender') },
        { id: 'metric-flagged',       label: 'Flagged Sims',   value: String(flaggedCount),                            unit: '',  sub: 'Require attention', color: '#FFCA28', actionLabel: 'Review', onAction: () => go && go('flagged-sims', 'Flagged Sims') },
    ];
    const visibleMetrics = orderedGroup('Performance').map((w) => metrics.find((m) => m.id === w.id)).filter(Boolean).filter((m) => vis(m.id));

    // Filter chips: sims from the hydrated catalogue, users by name + email.
    // Same FilterChip control as the dimension row above; active values come
    // up pre-ticked.
    const simScope = normScope(s.sim) || [];
    const userScope = normScope(s.user) || [];
    const simOptions = useMemo(
        () => SIMS.map((sim) => ({ label: sim.name, value: sim.name })).sort((a, b) => a.label.localeCompare(b.label)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DATA_EPOCH],
    );
    const userOptions = useMemo(
        () => USERS.map((u) => ({ label: u.email ? `${u.name} (${u.email})` : u.name, value: u.email || u.name, display: u.name })).sort((a, b) => a.label.localeCompare(b.label)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DATA_EPOCH],
    );
    const userDisplay = (v) => userOptions.find((o) => o.value === v)?.display || v;

    return (
        <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FilterChip
                    label="Simulation" options={simOptions} activeValues={simScope} searchPlaceholder="Search simulations"
                    onApply={({ added, removed }) => {
                        removed.forEach((v) => removeScope && removeScope('sim', v));
                        added.forEach((v) => drill && drill('sims', v, { type: 'sim', value: v }));
                    }}
                />
                <FilterChip
                    label="User" options={userOptions} activeValues={userScope} searchPlaceholder="Search users"
                    onApply={({ added, removed }) => {
                        removed.forEach((v) => removeScope && removeScope('user', v));
                        added.forEach((v) => drill && drill('sims', userDisplay(v), { type: 'user', value: v }));
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
                </Box>
            </Box>

            {/* Performance, KPI strip. Headline numbers up top so the
                reader scans state before diving into trends or breakdowns.
                Cards reflow to fill the row when peers are hidden so the
                section never carries dead space. */}
            {visibleMetrics.length > 0 && (() => {
                const metricMd = Math.max(3, Math.floor(12 / visibleMetrics.length));
                const metricXs = visibleMetrics.length === 1 ? 12 : 6;
                return (
                    <>
                        <SectionTitle>Performance</SectionTitle>
                        <Grid container spacing={1}>
                            {visibleMetrics.map((m) => (
                                <Grid item xs={metricXs} md={metricMd} key={m.label}>
                                    <MetricCard {...m} />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                );
            })()}

            {/* Activity & breakdowns: the volume/device row up top, then the
                "where are clicks coming from" row beneath, all under a single
                section title so the page reads as one block. */}
            {(() => {
                const topVisible = vis('clicks-by-month') || vis('device-breakdown');
                const chartOrder = orderedGroup('Activity & breakdowns').map((w) => w.id);
                // click-by-dept renders first, then the click-by-sim/heatmap stack,
                // then sim-engagement closes the row on the right.
                const soloLeft = ['click-by-dept'];
                const soloRight = ['sim-engagement'];
                const chartCols = chartOrder.filter((id) => ['click-by-sim', 'heatmap-card', ...soloLeft, ...soloRight].includes(id) && vis(id));
                if (!topVisible && chartCols.length === 0) return null;
                const renderChart = (id) => {
                    if (id === 'click-by-sim') return (
                        <MainCard key={id} content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: 210, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>Click Rate by Simulation</Typography>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Top 5 of {SIMS.length} - size proportional to click rate - hover for detail</Typography>
                            </Box>
                            <Box sx={{ px: 3, pb: 3, pt: 1.5, flex: 1, minHeight: 0, display: 'flex', alignItems: 'center' }}><ClickRateChart theme={theme} log={scopedLogs} simsAgg={simRows} /></Box>
                        </MainCard>
                    );
                    if (id === 'heatmap-card') return <Box key={id}><HeatmapCard go={go} period={period} startDate={startDate} endDate={endDate} log={scopedLogs} heatmapAgg={simHeatAgg} /></Box>;
                    if (id === 'click-by-dept') return <ClickRateByDept key={id} theme={theme} period={period} scope={scope} drill={drill} log={scopedLogs} serverScope={(hasServerScope && !clientOnlyScope) ? serverScope : undefined} />;
                    if (id === 'sim-engagement') return <FunnelCard key={id} period={period} startDate={startDate} endDate={endDate} log={scopedLogs} agg={simAgg} />;
                    return null;
                };
                // click-by-sim and heatmap-card share a column; others get their own
                const stackIds = chartOrder.filter((id) => ['click-by-sim', 'heatmap-card'].includes(id) && vis(id));
                const leftIds = soloLeft.filter((id) => vis(id));
                const rightIds = soloRight.filter((id) => vis(id));
                const soloIds = [...leftIds, ...rightIds];
                // build columns in order: insert the stacked column where click-by-sim or heatmap-card first appears
                const firstStackPos = chartOrder.findIndex((id) => stackIds.includes(id));
                const orderedCols = [...soloIds.map((id) => ({ type: 'solo', id }))];
                if (stackIds.length > 0) orderedCols.splice(Math.min(firstStackPos, orderedCols.length), 0, { type: 'stack' });
                return (
                    <>
                        <SectionTitle>Activity & breakdowns</SectionTitle>
                        {topVisible && (
                            <Grid container spacing={1} alignItems="stretch" justifyContent="flex-start">
                                {vis('clicks-by-month') && (
                                    <Grid item xs={12} md={vis('device-breakdown') ? 8 : 12} sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <motion.div variants={fadeUp} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <ClicksByMonthChart period={period} startDate={startDate} endDate={endDate} theme={theme} log={scopedLogs} trendsAgg={simTrendAgg} />
                                        </motion.div>
                                    </Grid>
                                )}
                                {vis('device-breakdown') && (
                                    <Grid item xs={12} md={vis('clicks-by-month') ? 4 : 12} sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <motion.div variants={fadeUp} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <DevicePieCard theme={theme} period={period} startDate={startDate} endDate={endDate} log={scopedLogs} deviceAgg={simDeviceAgg} />
                                        </motion.div>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                        {chartCols.length > 0 && (
                            <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                                {orderedCols.map((col) => (
                                    <Grid key={col.type === 'stack' ? 'stack' : col.id} item xs={12} md={4}>
                                        <motion.div variants={fadeUp} style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {col.type === 'stack' ? stackIds.map((id) => renderChart(id)) : renderChart(col.id)}
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                );
            })()}

            {/* Click and report, the trend chart pairs naturally with the
                Click vs Report table; together they answer "are we improving
                and which templates draw the most click vs report engagement". */}
            {(vis('report-trend') || vis('high-risk')) && (
                <>
                <SectionTitle>Click and report</SectionTitle>
                <Grid container spacing={1} alignItems="stretch" justifyContent="flex-start">
                    {vis('report-trend') && (
                        <Grid item xs={12} md={vis('high-risk') ? 6 : 12}>
                            <motion.div variants={fadeUp} style={{ height: '100%' }}>
                                <ReportingTrendCard period={period} startDate={startDate} endDate={endDate} theme={theme} log={scopedLogs} trendsAgg={simTrendAgg} />
                            </motion.div>
                        </Grid>
                    )}
                    {vis('high-risk') && (() => {
                        // Simulation Engagement: top sims in the current scope by total
                        // user interaction (clicks + reports). Categories renamed from
                        // the old High Click Users table to read as a sim-level surface.
                        const engagementRows = [...scopedSims]
                            .map((s) => ({
                                ...s,
                                clickPct: parseFloat(s.click) || 0,
                                reportPct: typeof s.reportRate === 'number' ? s.reportRate : (parseFloat(s.reportRate) || 0),
                            }))
                            .sort((a, b) => (b.clickPct + b.reportPct) - (a.clickPct + a.reportPct))
                            .slice(0, 6);
                        return (
                        <Grid item xs={12} md={vis('report-trend') ? 6 : 12}>
                            <motion.div variants={fadeUp} style={{ height: '100%' }}>
                                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                                    <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>Click vs Report</Typography>
                                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Top sims by user engagement. Click rate and report rate side by side.</Typography>
                                        </Box>
                                        {/* Rows drill into each sim; the full list is this same Sims page, so no redundant "view all" nav. */}
                                    </Box>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                                        <TableHeader columns={['Simulation', 'Click Rate', 'Report Rate']} template="1fr 110px 110px" actions />
                                        <motion.div variants={stagger} initial="hidden" animate="visible">
                                            {engagementRows.map((s, i) => (
                                                <motion.div key={s.name || i} variants={fadeUp}>
                                                    <TableRow template="1fr 110px 110px" onClick={() => drill('sim-detail', s.name, { type: 'sim', value: s.name })}>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</Typography>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: s.lv === 'high' ? '#FF5252' : s.lv === 'med' ? '#FFCA28' : '#66BB6A' }}>
                                                            {s.click}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: s.reportPct > 0 ? '#66BB6A' : 'text.disabled' }}>
                                                            {s.reportPct != null ? `${s.reportPct}%` : '-'}
                                                        </Typography>
                                                    </TableRow>
                                                </motion.div>
                                            ))}
                                        </motion.div>
                                    </Box>
                                </MainCard>
                            </motion.div>
                        </Grid>
                        );
                    })()}
                </Grid>
                </>
            )}

            {/* Lure effectiveness: ranks tag-level click rate for the
                current scope. Sits between click-and-report and detail
                tables so the reader gets the "why" before the row-level
                drill-in. */}
            {(vis('lure-effectiveness') || vis('time-to-click')) && (
                <>
                    <SectionTitle>Lure analysis</SectionTitle>
                    <Grid container spacing={2}>
                        {vis('lure-effectiveness') && (
                            <Grid item xs={12} md={vis('time-to-click') ? 6 : 12}>
                                <LureEffectivenessCard events={scopedLogs} tagRows={lureAggRows || lureTagRows} />
                            </Grid>
                        )}
                        {vis('time-to-click') && (
                            <Grid item xs={12} md={vis('lure-effectiveness') ? 6 : 12}>
                                <TimeToClickCard events={scopedLogs} ttcAgg={simTtcAgg} />
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {/* Detail tables, row-level drill-in. Rendered in stored
                order so the customise drawer's section order matches the
                page; if every table is hidden the section title vanishes
                with them. */}
            {(() => {
                const tablesGroup = orderedGroup('Detail tables');
                const visibleTables = tablesGroup.filter((w) =>
                    vis(w.id) && !(w.id === 'users-table' && scope?.user)
                );
                if (visibleTables.length === 0) return null;
                return <SectionTitle>Detail tables</SectionTitle>;
            })()}
            {orderedGroup('Detail tables').map((w) => {
                if (!vis(w.id)) return null;
                if (w.id === 'all-sims') {
                    const SIMS_TOP_N = 10;
                    const topSims = [...scopedSims].sort((a, b) => parseInt(b.click) - parseInt(a.click)).slice(0, SIMS_TOP_N);
                    return (
                    <motion.div key="all-sims" variants={fadeUp}>
                        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                            <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>All Simulations</Typography>
                                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Top {Math.min(SIMS_TOP_N, scopedSims.length)} of {scopedSims.length} by click rate - click a row to view detail</Typography>
                                </Box>
                                {scopedSims.length > SIMS_TOP_N && (
                                    <Button
                                        size="small" variant="outlined"
                                        onClick={() => drill('all-sims-table', 'All Simulations')}
                                        sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                                    >
                                        View all ({scopedSims.length})
                                    </Button>
                                )}
                            </Box>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                                <TableHeader columns={['Simulation', 'Sends', 'Click Rate', 'Report Rate', 'Difficulty']} template="1fr 80px 100px 100px 90px" actions />
                                <motion.div variants={stagger} initial="hidden" animate="visible">
                                    {topSims.map((s, i) => (
                                        <motion.div key={i} variants={fadeUp}>
                                            <TableRow template="1fr 80px 100px 100px 90px" onClick={() => drill('sim-detail', s.name, { type: 'sim', value: s.name })}>
                                                <Box>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>{s.name}</Typography>
                                                    {(() => {
                                                        const tagKeys = tagsForSim(s.name);
                                                        if (tagKeys.length === 0) return null;
                                                        return (
                                                            <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mt: '3px' }}>
                                                                {tagKeys.slice(0, 3).map((t) => (
                                                                    <Box key={t} sx={{ fontSize: 9, fontWeight: 500, px: '6px', py: '1px', borderRadius: '3px', bgcolor: 'action.hover', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }}>
                                                                        {tagLabel(t)}
                                                                    </Box>
                                                                ))}
                                                                {tagKeys.length > 3 && (
                                                                    <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>+{tagKeys.length - 3}</Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    })()}
                                                    {s.flag && <Typography sx={{ fontSize: 10, color: '#FF5252', fontWeight: 600, mt: '1px' }}>! Flagged</Typography>}
                                                </Box>
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{s.sent?.toLocaleString() ?? ', '}</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: s.lv === 'high' ? '#FF5252' : s.lv === 'med' ? '#FFCA28' : '#66BB6A' }}>{s.click}</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#66BB6A' }}>{s.reportRate != null ? `${s.reportRate}%` : ', '}</Typography>
                                                <DiffDots diff={s.diff} />
                                            </TableRow>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            </Box>
                        </MainCard>
                    </motion.div>
                    );
                }
                if (w.id === 'users-table' && scope?.user) return null;
                // This table is the page's one raw-sourced widget (HIGH_RISK_USERS) unless a user-dim
                // scope routes it through the `users` agg. While the background raw hydration is still
                // running, show a placeholder instead of "Top 0 of 0" (the dataVersion remount fills it).
                if (w.id === 'users-table' && !dataReady && !(hasServerScope && !clientOnlyScope)) {
                    return (
                        <motion.div key="users-table" variants={fadeUp}>
                            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                                <Box sx={{ px: 3, py: 4, textAlign: 'center', color: 'text.secondary', fontSize: 12 }}>Loading users…</Box>
                            </MainCard>
                        </motion.div>
                    );
                }
                if (w.id === 'users-table') {
                    const USERS_TOP_N = 10;
                    // Same row shape + columns as the High Click Users drill,
                    // ranked by click rate so the preview is its literal top 10.
                    const riskByEmail = new Map(USERS.map((u) => [u.email, u.risk]));
                    const cardUsers = scopedUsersTable.map((u) => {
                        const sent = u.sent || 0;
                        return {
                            name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
                            email: u.email,
                            risk: u.score ?? riskByEmail.get(u.email) ?? null,
                            sent, clicked: u.clicked || 0, secondStage: u.secondStage || 0, reported: u.reported || 0,
                            clickRate: sent ? Math.round(((u.clicked || 0) / sent) * 100) : 0,
                            reportRate: sent ? Math.round(((u.reported || 0) / sent) * 100) : 0,
                        };
                    }).sort((a, b) => (b.clickRate - a.clickRate) || (b.clicked - a.clicked)).slice(0, USERS_TOP_N);
                    return (
                    <motion.div key="users-table" variants={fadeUp}>
                        <DataTable
                            title="Users"
                            subtitle={`Top ${Math.min(USERS_TOP_N, scopedUsersTable.length)} of ${scopedUsersTable.length} by click rate - click a row to view activity log`}
                            columns={highClickUserColumns()}
                            rows={cardUsers}
                            rowKey={(u) => u.email}
                            onRowClick={(u) => drill('user-detail', u.name, { type: 'user', value: u.email })}
                            initialSort={{ by: 'clickRate', dir: 'desc' }}
                            storageKey="rbx-sims-users-card-cols"
                            pagination={false}
                            emptyMessage="No users in scope"
                            headerActions={scopedUsersTable.length > USERS_TOP_N ? (
                                <Button
                                    size="small" variant="outlined"
                                    onClick={() => drill('high-risk-users', 'High Click Users')}
                                    sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                                >
                                    View all ({scopedUsersTable.length})
                                </Button>
                            ) : null}
                        />
                    </motion.div>
                    );
                }
                if (w.id === 'send-history') return (
                    <motion.div key="send-history" variants={fadeUp}>
                        <SendHistoryCard drill={drill} logs={scopedLogs} total={simAgg ? simAgg.sent : null} />
                    </motion.div>
                );
                return null;
            })}

        </motion.div>
    );
}
